﻿import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SignaturePad } from "@/components/oprema/PrimopredajaStep";
import { Feedback, useResource } from "@/components/workflow/shared";
import { datum } from "@/lib/format";
import {
  equipmentE2Api,
  type EquipmentLookup,
  type Inventory,
  type InventoryItem,
  type E2Result,
} from "@/lib/api/oprema-e2";
import { conditionNames, Field, Photos, useEquipmentAccess } from "./shared";
import { EquipmentBack, useEquipmentNavigation } from "./EquipmentNavigation";
import { EquipmentDraft, useEquipmentDraftState, type EquipmentDraftCache } from "./EquipmentDraft";
import {
  E2DocumentActions,
  E2Errors,
  E2Notice,
  ProcessState,
  useE2Save,
} from "./E2Shared";

const inventoryNames = {
  ad_hoc: "Ad hoc",
  periodicka: "Periodička",
  zavrsna: "Završna",
};
type CountDraft = Pick<
  InventoryItem,
  "izbrojano" | "rjesenje" | "razlog" | "opis" | "iz_mjesta_id" | "dokumenti"
>;
export function inventoryReady(lines: InventoryItem[]) {
  return lines.every((line) => {
    if (
      line.izbrojano === null ||
      !Number.isInteger(line.izbrojano) ||
      line.izbrojano < 0 ||
      (line.komad_id && line.izbrojano > 1)
    )
      return false;
    const difference = line.izbrojano - line.ocekivano;
    if (!difference) return true;
    const matchingResolution =
      difference > 0
        ? (line.rjesenje === "pronadeno" ||
            line.rjesenje === "doslo_s_drugog") &&
          line.razlog === line.rjesenje
        : (line.rjesenje === "nestalo" || line.rjesenje === "osteceno") &&
          ["manjak", "krivo_upisano_ranije", "drugo"].includes(
            line.razlog ?? "",
          );
    return (
      matchingResolution &&
      (line.razlog !== "drugo" || !!line.opis?.trim()) &&
      line.dokumenti.length > 0 &&
      (line.rjesenje !== "doslo_s_drugog" || !!line.iz_mjesta_id)
    );
  });
}

export function InventoryViews({
  placeId,
  lookup,
}: {
  placeId?: string;
  lookup: EquipmentLookup;
}) {
  const access = useEquipmentAccess(),
    save = useE2Save();
  const navigation = useEquipmentNavigation();
  const selected = navigation.params.get("inventura") ?? "";
  const setSelected = (id: string) =>
    id ? navigation.open({ inventura: id }) : navigation.close(["inventura"]);
  const [cache] = useState<EquipmentDraftCache>(() => new Map());
  const [kind, setKind] = useState<Inventory["vrsta"]>("ad_hoc"),
    [place, setPlace] = useState(placeId ?? "");
  const list = useResource(
    `inventories:${placeId}`,
    useCallback(
      (signal) => equipmentE2Api.inventories(placeId, signal),
      [placeId],
    ),
  );
  const detail = useResource(
    `inventory:${selected}`,
    useCallback(
      (signal) =>
        selected
          ? equipmentE2Api.inventory(selected, signal)
          : Promise.resolve(null),
      [selected],
    ),
  );
  const [operation, setOperation] = useState<E2Result | null>(null);
  const reload = () => {
    detail.reload();
    list.reload();
  };
  return (
    <section aria-label="Inventura">
      <h2 className="text-xl font-bold">Inventura</h2>
      <Feedback error={list.error || detail.error} />
      <E2Notice
        result={operation}
        message="Inventura spremljena."
        onUndone={() => {
          setOperation(null);
          reload();
        }}
      />
      {!selected ? (
        <>
          {access.damage && (
            <form
              className="equipment-form"
              aria-label="Započni inventuru"
              onSubmit={(e) => {
                e.preventDefault();
                void save.run(
                  () => equipmentE2Api.startInventory(place, kind),
                  (row) => {
                    setSelected(row.id);
                    list.reload();
                  },
                );
              }}
            >
              <fieldset className="equipment-step is-current">
                <legend><span className="equipment-step-number">1</span> Mjesto i vrsta inventure</legend>
              <Field label="Mjesto inventure">
                <select
                  required
                  value={place}
                  disabled={!!placeId}
                  onChange={(e) => setPlace(e.target.value)}
                >
                  <option value="">Odaberi mjesto</option>
                  {lookup.places
                    .filter((p) => access.all || !!p.gradiliste_id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.naziv}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Vrsta inventure">
                <select
                  value={kind}
                  onChange={(e) =>
                    setKind(e.target.value as Inventory["vrsta"])
                  }
                >
                  {Object.entries(inventoryNames).map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </Field>
              </fieldset>
              <E2Errors save={save} />
              <Button type="submit" disabled={!place || save.busy}>
                Započni inventuru
              </Button>
            </form>
          )}
          {list.loading && <p role="status">Učitavanje inventura…</p>}
          {list.data?.map((row) => (
            <Button variant="outline" className="equipment-placecard" key={row.id} onClick={() => setSelected(row.id)} aria-label={`Otvori inventuru: ${lookup.places.find((p) => p.id === row.mjesto_id)?.naziv ?? "Inventura"} · ${inventoryNames[row.vrsta]}`}>
              <span className="equipment-list-name"><b>
                {lookup.places.find((p) => p.id === row.mjesto_id)?.naziv ??
                  "Inventura"}{" "}
                · {inventoryNames[row.vrsta]}
              </b>
              <small>{datum(row.created_at)}</small></span>
              <Badge tone={row.status === "zavrsena" ? "ok" : row.status === "ceka_upravu" ? "warn" : "info"}>{row.status === "zavrsena" ? "Završeno" : row.status === "ceka_upravu" ? "Čeka upravu" : "U tijeku"}</Badge>
              <span aria-hidden="true">›</span>
            </Button>
          ))}
          {list.data?.length === 0 && (
            <p>Mjesto još nema inventuru. Nepopisano stanje nije potvrđeno.</p>
          )}
        </>
      ) : (
        <>
          <EquipmentBack crumbs={[{ label: "Inventura", to: "/oprema/inventure" }, { label: "Brojanje" }]} onBack={() => setSelected("")} />
          <Button variant="outline" onClick={() => setSelected("")}>
            Sve inventure
          </Button>
          {detail.loading && <p role="status">Učitavanje inventure…</p>}
          {detail.data && (
            <EquipmentDraft cache={cache} name={detail.data.id}>
            <InventoryDetail
              key={`${detail.data.id}:${detail.data.updated_at ?? detail.data.status}:${detail.data.promijenjeno}`}
              inventory={detail.data}
              lookup={lookup}
              reload={reload}
              saved={(row) => {
                if (row.rezultat) setOperation(row.rezultat);
                reload();
              }}
            />
            </EquipmentDraft>
          )}
        </>
      )}
    </section>
  );
}

export function InventoryDetail({
  inventory,
  lookup,
  reload,
  saved,
}: {
  inventory: Inventory;
  lookup: EquipmentLookup;
  reload: () => void;
  saved: (row: Inventory) => void;
}) {
  const { user } = useAuth(),
    access = useEquipmentAccess(),
    save = useE2Save();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useEquipmentDraftState<Record<string, Partial<CountDraft>>>("counts", {}),
    [photos, setPhotos] = useEquipmentDraftState<Record<string, File[]>>("photos", {});
  const [signature, setSignature] = useEquipmentDraftState<File | null>("signature", null),
    [decision, setDecision] = useEquipmentDraftState("decision", "");
  const [extraArticle, setExtraArticle] = useEquipmentDraftState("extraArticle", ""),
    [extraPiece, setExtraPiece] = useEquipmentDraftState("extraPiece", ""),
    [extraRental, setExtraRental] = useEquipmentDraftState("extraRental", "");
  const editable = inventory.status === "u_tijeku" && access.damage;
  const merged = inventory.stavke.map((line) => ({
    ...line,
    ...drafts[line.id],
    dokumenti: photos[line.id]?.length
      ? ["new-photo"]
      : (drafts[line.id]?.dokumenti ?? line.dokumenti),
  }));
  const rentals = useResource(
    `inventory-rentals:${editable}`,
    useCallback(
      (signal) =>
        editable ? equipmentE2Api.rentals(signal) : Promise.resolve([]),
      [editable],
    ),
  );
  const counted = merged.filter((l) => l.izbrojano !== null).length;
  const differences = merged.filter(
    (l) => l.izbrojano !== null && l.izbrojano !== l.ocekivano,
  );
  const ready = inventoryReady(merged);
  const ownDecision =
    inventory.zavrsio_id === user?.id ||
    inventory.stavke.some(
      (line) =>
        line.izbrojano !== null &&
        line.izbrojano !== line.ocekivano &&
        line.brojio_id === user?.id,
    );

  function patch(id: string, data: Partial<CountDraft>) {
    setDrafts((old) => ({ ...old, [id]: { ...old[id], ...data } }));
  }
  async function persistCounts() {
    const lines = [];
    for (const line of merged) {
      if (line.izbrojano === null) continue;
      const uploaded = photos[line.id]?.length
        ? await equipmentE2Api.uploadPhotos(photos[line.id]!)
        : line.dokumenti;
      lines.push({
        id: line.id,
        izbrojano: line.izbrojano,
        rjesenje: line.rjesenje,
        razlog: line.razlog,
        opis: line.opis || null,
        iz_mjesta_id: line.iz_mjesta_id || null,
        dokumenti: uploaded,
      });
    }
    return lines.length
      ? equipmentE2Api.countInventory(inventory.id, lines)
      : inventory;
  }
  return (
    <div className="equipment-form">
      <ProcessState state={inventory.status}>
        <span>
          {inventoryNames[inventory.vrsta]} ·{" "}
          {lookup.places.find((p) => p.id === inventory.mjesto_id)?.naziv}
        </span>
      </ProcessState>
      {inventory.promijenjeno && (
        <p role="alert" className="equipment-warning">
          Promijenjeno tijekom inventure. Pregledajte osvježeno očekivano stanje
          i ponovno provjerite brojanje.
        </p>
      )}
      <section className="equipment-card" aria-label="Sljedeći korak">
        <h3 className="font-bold">Sljedeći korak</h3>
        <p>
          {counted === merged.length ? "✓" : "✗"} Popisano {counted} od{" "}
          {merged.length} stavki
        </p>
        <p>
          {ready ? "✓" : "✗"} Razlike imaju rješenje, razlog i fotku brojanja
        </p>
        {editable && (
          <Button
            variant="outline"
            disabled={save.busy}
            onClick={() =>
              void save.run(
                async () => {
                  await persistCounts();
                  return equipmentE2Api.refreshInventory(inventory.id);
                },
                () => {
                  setDrafts({});
                  setPhotos({});
                  reload();
                },
              )
            }
          >
            Osvježi očekivano stanje
          </Button>
        )}
      </section>
      <form
        className="equipment-form"
        aria-label="Brojanje inventure"
        onSubmit={(e) => {
          e.preventDefault();
          if (!editable || save.busy) return;
          void save.run(persistCounts, () => {
            setDrafts({});
            setPhotos({});
            reload();
          });
        }}
      >
        <fieldset className={`equipment-step ${counted === merged.length ? "is-done" : "is-current"}`}>
          <legend><span className="equipment-step-number">1</span> Brojanje opreme</legend>
        {merged.map((line) => {
          const diff =
            line.izbrojano === null ? null : line.izbrojano - line.ocekivano;
          return (
            <article className={`equipment-transfer-line ${line.komad_id ? "" : "equipment-inventory-count"}`} key={line.id}>
              <b>
                {line.naziv}
                {line.inventarni_broj ? ` · ${line.inventarni_broj}` : ""}
              </b>
              <p>
                <Badge tone={line.najam_id ? "info" : "neutral"}>
                  {line.najam_id
                    ? `Najam · ${line.najmodavac ?? "Najmodavac"}`
                    : "Vlastito"}
                </Badge>{" "}
                · {conditionNames[line.stanje]} · očekivano {line.ocekivano}
              </p>
              {line.komad_id ? (
                <label className="equipment-check">
                  <input
                    disabled={!editable || save.busy}
                    type="checkbox"
                    aria-label={`Nađeno: ${line.inventarni_broj ?? line.naziv}`}
                    checked={line.izbrojano === 1}
                    onChange={(e) =>
                      patch(line.id, { izbrojano: e.target.checked ? 1 : 0 })
                    }
                  />
                  ✓ Nađeno
                </label>
              ) : (
                <Field
                  label="Izbrojano"
                >
                  <input
                    aria-label={`Izbrojano: ${line.naziv} · ${line.najam_id ? "najam" : "vlastito"} · ${conditionNames[line.stanje]}`}
                    required
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    disabled={!editable || save.busy}
                    value={line.izbrojano ?? ""}
                    onChange={(e) =>
                      patch(line.id, {
                        izbrojano:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </Field>
              )}
              {line.komad_id && line.izbrojano === null && editable && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => patch(line.id, { izbrojano: 0 })}
                >
                  Nije nađeno: {line.inventarni_broj}
                </Button>
              )}
              {diff === null ? (
                <Badge tone="neutral">Nije izbrojano</Badge>
              ) : (
                <Badge tone={diff === 0 ? "ok" : diff < 0 ? "bad" : "info"}>
                  {diff === 0
                    ? "✓ Bez razlike"
                    : `Razlika ${diff > 0 ? "+" : ""}${diff}`}
                </Badge>
              )}

            </article>
          );
        })}
        </fieldset>
        <fieldset disabled={counted < merged.length || save.busy} className={`equipment-step ${ready ? "is-done" : counted < merged.length ? "is-future" : "is-current"}`}>
          <legend><span className="equipment-step-number">2</span> Razlike i dokazi</legend>
          {!differences.length && <p>{counted === merged.length ? "✓ Nema razlika." : "Prvo izbroji opremu. Razlike se prikazuju ovdje."}</p>}
          {differences.map((line) => {
            const diff = line.izbrojano! - line.ocekivano;
            const resolutions = diff < 0
              ? [
                  ["trazi_dalje", "Traži dalje"],
                  ["nestalo", "Nestalo"],
                  ...(line.stanje === "ispravno" ? [["osteceno", "Oštećeno"]] : []),
                ]
              : [["pronadeno", "Pronađeno"], ["doslo_s_drugog", "Došlo s drugog gradilišta"]];
            const reasons = diff > 0
              ? [["pronadeno", "Pronađeno"], ["doslo_s_drugog", "Došlo s drugog gradilišta"]]
              : [["manjak", "Manjak"], ["krivo_upisano_ranije", "Krivo upisano ranije"], ["drugo", "Drugo"]];
            return <article className="equipment-step" key={line.id}>
              <b>{line.naziv}{line.inventarni_broj ? ` · ${line.inventarni_broj}` : ""}</b>
              <Badge tone={diff < 0 ? "bad" : "info"}>Razlika {diff > 0 ? "+" : ""}{diff}</Badge>
                <>
                  <Field label={`Rješenje: ${line.naziv}`}>
                    <div className="equipment-choices">
                      {resolutions.map(([value, label]) => (
                        <Button type="button" variant="outline" key={value} disabled={!editable || save.busy} aria-pressed={line.rjesenje === value} onClick={() => patch(line.id, {
                          rjesenje: value as InventoryItem["rjesenje"],
                          ...(diff > 0 ? { razlog: value as InventoryItem["razlog"] } : {}),
                          ...(value !== "doslo_s_drugog" ? { iz_mjesta_id: null } : {}),
                        })}>{label}</Button>
                      ))}
                    </div>
                  </Field>
                  {line.rjesenje === "doslo_s_drugog" && (
                    <Field label={`Došlo s: ${line.naziv}`}>
                      <select
                        required
                        disabled={!editable}
                        value={line.iz_mjesta_id ?? ""}
                        onChange={(e) =>
                          patch(line.id, { iz_mjesta_id: e.target.value })
                        }
                      >
                        <option value="">Odaberi gradilište</option>
                        {lookup.places
                          .filter((p) => p.id !== inventory.mjesto_id)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.naziv}
                            </option>
                          ))}
                      </select>
                    </Field>
                  )}
                  <Field label={`Razlog razlike: ${line.naziv}`}>
                    <div className="equipment-choices">
                      {reasons.map(([value, label]) => (
                        <Button type="button" variant="outline" disabled={!editable || save.busy} key={value} aria-pressed={line.razlog === value} onClick={() => patch(line.id, { razlog: value as InventoryItem["razlog"] })}>{label}</Button>
                      ))}
                    </div>
                  </Field>
                  <Field label={`Opis razlike: ${line.naziv}`}>
                    <textarea
                      disabled={!editable}
                      required={line.razlog === "drugo"}
                      value={line.opis ?? ""}
                      maxLength={2000}
                      onChange={(e) => patch(line.id, { opis: e.target.value })}
                    />
                  </Field>
                  {editable && (
                    <>
                      <Photos
                        files={photos[line.id] ?? []}
                        onChange={(files) =>
                          setPhotos((old) => ({ ...old, [line.id]: files }))
                        }
                        required={!line.dokumenti.length}
                      />
                      <p className="text-sm text-muted-foreground">
                        Nova fotka provjerava se i veže uz stavku kada spremite
                        brojanje.
                      </p>
                    </>
                  )}
                  {line.dokumenti.length > 0 && !photos[line.id]?.length && (
                    <p>Fotka je priložena uz brojanje.</p>
                  )}
                </>
            </article>;
          })}
        </fieldset>
        {editable && (
          <Button
            type="submit"
            variant={ready ? "outline" : "default"}
            disabled={save.busy || counted === 0}
          >
            Spremi brojanje
          </Button>
        )}
      </form>
      {editable && (
        <details>
          <summary className="equipment-link">
            Dodaj nađenu opremu izvan popisa
          </summary>
          <form
            className="equipment-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run(
                async () => {
                  const piece = extraPiece.trim()
                    ? await equipmentE2Api.findPiece(extraPiece.trim())
                    : null;
                  return equipmentE2Api.extraInventory(inventory.id, {
                    artikl_id: extraArticle,
                    komad_id: piece?.id ?? null,
                    najam_id: piece?.najam_id ?? (extraRental || null),
                    stanje: "ispravno",
                  });
                },
                () => {
                  setExtraArticle("");
                  setExtraPiece("");
                  setExtraRental("");
                  reload();
                },
              );
            }}
          >
            <Field label="Nađeni artikl">
              <select
                required
                value={extraArticle}
                onChange={(e) => setExtraArticle(e.target.value)}
              >
                <option value="">Odaberi artikl</option>
                {lookup.articles.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.naziv}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Inventarni broj komada (za opremu po komadu)">
              <input
                value={extraPiece}
                onChange={(e) => setExtraPiece(e.target.value)}
                placeholder="INV-0001"
              />
            </Field>
            <Field label="Najam (neobavezno)">
              <select
                value={extraRental}
                onChange={(e) => setExtraRental(e.target.value)}
              >
                <option value="">Vlastito</option>
                {rentals.data
                  ?.filter((r) => r.status !== "zatvoren")
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.najmodavac} · {r.broj_ugovora}
                    </option>
                  ))}
              </select>
            </Field>
            <Button disabled={!extraArticle || save.busy}>
              Dodaj u inventuru
            </Button>
          </form>
        </details>
      )}
      {inventory.status === "u_tijeku" && access.maintain && (
        <fieldset className={`equipment-step ${ready ? "is-current" : "is-future"}`}>
          <legend><span className="equipment-step-number">3</span> Završetak inventure</legend>
          <p>
            {differences.length} razlika. Razlike iznad praga i komade potvrđuje
            druga osoba iz uprave.
          </p>
          {ready && <SignaturePad
            label="Potpis voditelja (neobavezno)"
            onChange={setSignature}
          />}
          <Button
            disabled={save.busy || !ready}
            onClick={() =>
              void save.run(async () => {
                await persistCounts();
                const id = signature
                  ? await equipmentE2Api.uploadSignature(signature)
                  : undefined;
                return equipmentE2Api.finishInventory(inventory.id, id);
              }, (row) => {
                setDrafts({});
                setPhotos({});
                setSignature(null);
                saved(row);
              })
            }
          >
            Završi inventuru i spremi zapisnik (PDF)
          </Button>
        </fieldset>
      )}
      {inventory.status === "ceka_upravu" && access.approve && (
        <fieldset className="equipment-step is-current">
          <legend><span className="equipment-step-number">3</span> Potvrda uprave</legend>
          {ownDecision && (
            <>
              <p>Zahtjev mora odobriti druga osoba.</p>
              <p>
                Odobravatelj ne smije završiti inventuru niti mijenjati stavke s
                razlikom.
              </p>
            </>
          )}
          <Field label="Razlog odluke">
            <textarea
              required
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              maxLength={2000}
            />
          </Field>
          <div className="equipment-actions">
            <Button
              disabled={save.busy || !decision.trim() || ownDecision}
              onClick={() =>
                void save.run(
                  () =>
                    equipmentE2Api.decideInventory(
                      inventory.id,
                      true,
                      decision,
                    ),
                  (row) => { setDecision(""); saved(row); },
                )
              }
            >
              Potvrdi inventuru
            </Button>
            <Button
              variant="outline"
              disabled={save.busy || !decision.trim()}
              onClick={() =>
                void save.run(
                  () =>
                    equipmentE2Api.decideInventory(
                      inventory.id,
                      false,
                      decision,
                    ),
                  (row) => { setDecision(""); saved(row); },
                )
              }
            >
              Odbij inventuru
            </Button>
          </div>
        </fieldset>
      )}
      {inventory.status === "zavrsena" && (
        <fieldset className="equipment-step is-done">
          <legend><span className="equipment-step-number">3</span> Inventura završena</legend>
          <p>✓ Brojanje i razlike su zaključeni. Zapisnik je u dnevniku.</p>
        </fieldset>
      )}
      {inventory.status === "ceka_upravu" && !access.approve && (
        <fieldset className="equipment-step is-future">
          <legend><span className="equipment-step-number">3</span> Potvrda uprave</legend>
          <p>Brojanje je spremljeno. Čeka se potvrda uprave.</p>
        </fieldset>
      )}
      <E2Errors save={save} />
      <details>
        <summary className="equipment-link">Dnevnik i zapisnik</summary>
        <p>
          Započeto {datum(inventory.created_at)}
          {inventory.zavrseno_at
            ? ` · Završeno ${datum(inventory.zavrseno_at)}`
            : ""}
        </p>
        {inventory.dokument && (
          <E2DocumentActions document={inventory.dokument} />
        )}
      </details>
      <Button variant="outline" onClick={() => navigate(`/oprema/mjesta/${inventory.mjesto_id}`)}>
        Oprema mjesta
      </Button>
    </div>
  );
}
